import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import HotelRefCard from '../components/HotelRefCard'
import FlightRefCard from '../components/FlightRefCard'
import AuthHeader from '../components/AuthHeader'
import { buildApiUrl } from '../config/api'

function CheckRedemptionValue() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('flights')
  const [flightSearchCriteria, setFlightSearchCriteria] = useState({
    from: '',
    to: '',
    cabinClass: 'economy'
  })
  const [hotelSearchCriteria, setHotelSearchCriteria] = useState({
    hotelName: '',
    city: ''
  })
  
  // Hotel autocomplete state
  const [showHotelDropdown, setShowHotelDropdown] = useState(false)
  const [filteredHotels, setFilteredHotels] = useState([])
  const [selectedHotelIndex, setSelectedHotelIndex] = useState(-1)
  
  const [searchResults, setSearchResults] = useState([])
  const [allResults, setAllResults] = useState([]) // Store all results from API
  const [displayedResults, setDisplayedResults] = useState([]) // Currently displayed results
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(3)
  const [aggregatedData, setAggregatedData] = useState({
    averageCpp: 0,
    totalRedemptions: 0,
    lowestPoints: 0,
    modePoints: 0
  })
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [globalStats, setGlobalStats] = useState({
    bestRecord: 0,
    totalRedemptions: 0,
    topPrograms: []
  })
  const [globalStatsLoading, setGlobalStatsLoading] = useState(false)

  // Fetch global statistics on component mount and when tab changes
  useEffect(() => {
    fetchGlobalStats()
  }, [activeTab])

  // Reset search results when switching tabs
  useEffect(() => {
    setSearchResults([])
    setAllResults([])
    setDisplayedResults([])
    setCurrentPage(1)
    setHasSearched(false)
    setAggregatedData({
      averageCpp: 0,
      totalRedemptions: 0,
      highestPoints: 0,
      lowestPoints: 0,
      modePoints: 0
    })
    // Reset hotel autocomplete state
    setShowHotelDropdown(false)
    setFilteredHotels([])
    setSelectedHotelIndex(-1)
  }, [activeTab])

  // Function to update displayed results based on current page
  const updateDisplayedResults = (results, page = 1) => {
    const endIndex = page * itemsPerPage
    const paginatedResults = results.slice(0, endIndex)
    setDisplayedResults(paginatedResults)
    setCurrentPage(page)
  }

  // Function to load more results
  const loadMoreResults = () => {
    const nextPage = currentPage + 1
    updateDisplayedResults(allResults, nextPage)
  }

  // Check if there are more results to load
  const hasMoreResults = () => {
    return displayedResults.length < allResults.length
  }

  const fetchGlobalStats = async () => {
    setGlobalStatsLoading(true)
    try {
      // Pass the current tab type to filter global stats
      const type = activeTab === 'flights' ? 'flight' : 'hotel';
      const response = await fetch(buildApiUrl(`/api/analytics/global-stats?type=${type}`))
      if (response.ok) {
        const data = await response.json()
        setGlobalStats(data)
      } else {
        // console.error('Failed to fetch global stats')
        // Set empty data instead of fake fallback data
        setGlobalStats({
          bestRecord: 0,
          totalRedemptions: 0,
          topPrograms: []
        })
      }
    } catch (error) {
      // console.error('Error fetching global stats:', error)
      // Set empty data on error instead of fake fallback data
      setGlobalStats({
        bestRecord: 0,
        totalRedemptions: 0,
        topPrograms: []
      })
    } finally {
      setGlobalStatsLoading(false)
    }
  }

  // Fetch hotel suggestions for autocomplete
  const fetchHotelSuggestions = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setFilteredHotels([])
      setShowHotelDropdown(false)
      return
    }

    try {
      // Search for existing hotel redemptions to get hotel names
      const response = await fetch(buildApiUrl('/api/redemptions/search?type=hotel&limit=10'))
      if (response.ok) {
        const data = await response.json()
        const hotels = data.redemptions || []
        
        // Filter hotels by name containing search term
        const matchingHotels = hotels
          .filter(redemption => redemption.hotel?.hotel?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
          .map(redemption => ({
            id: redemption.hotel.hotel.id,
            name: redemption.hotel.hotel.name,
            city: redemption.hotel.hotel.city,
            country: redemption.hotel.hotel.country,
            brand: redemption.hotel.hotel.brand,
            hotel_group: redemption.hotel.hotel.hotel_group
          }))
          .filter((hotel, index, self) => 
            // Remove duplicates based on hotel name
            index === self.findIndex(h => h.name === hotel.name)
          )
          .slice(0, 8) // Limit to 8 suggestions
        
        setFilteredHotels(matchingHotels)
        setShowHotelDropdown(matchingHotels.length > 0)
        setSelectedHotelIndex(-1)
      } else {
        setFilteredHotels([])
        setShowHotelDropdown(false)
      }
    } catch (error) {
      // console.error('Error fetching hotel suggestions:', error)
      setFilteredHotels([])
      setShowHotelDropdown(false)
    }
  }

  // Handle hotel name input change
  const handleHotelNameChange = (value) => {
    setHotelSearchCriteria(prev => ({ ...prev, hotelName: value }))
    
    if (value.trim().length >= 2) {
      fetchHotelSuggestions(value)
    } else {
      setFilteredHotels([])
      setShowHotelDropdown(false)
    }
    setSelectedHotelIndex(-1)
  }

  // Handle hotel selection from dropdown
  const handleHotelSelect = (hotel) => {
    setHotelSearchCriteria(prev => ({ ...prev, hotelName: hotel.name }))
    setShowHotelDropdown(false)
    setFilteredHotels([])
    setSelectedHotelIndex(-1)
  }

  // Handle keyboard navigation for hotel dropdown
  const handleHotelKeyDown = (e) => {
    if (!showHotelDropdown || filteredHotels.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedHotelIndex(prev => 
          prev < filteredHotels.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedHotelIndex(prev => 
          prev > 0 ? prev - 1 : filteredHotels.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedHotelIndex >= 0 && selectedHotelIndex < filteredHotels.length) {
          handleHotelSelect(filteredHotels[selectedHotelIndex])
        }
        break
      case 'Escape':
        setShowHotelDropdown(false)
        setSelectedHotelIndex(-1)
        break
    }
  }

  const cabinClassOptions = [
    { value: 'economy', label: 'Economy' },
    { value: 'premium_economy', label: 'Premium Economy' },
    { value: 'business', label: 'Business' },
    { value: 'first', label: 'First Class' }
  ]

  const handleFlightSearch = async () => {
    if (!flightSearchCriteria.from || !flightSearchCriteria.to) {
      alert('Please fill in both origin and destination')
      return
    }

    setLoading(true)
    setHasSearched(true)

    try {
      // Build query parameters for flights
      const params = new URLSearchParams({
        from: flightSearchCriteria.from,
        to: flightSearchCriteria.to,
        cabin_class: flightSearchCriteria.cabinClass,
        type: 'flight',
        orderBy: 'created_at',
        sort: 'desc'
      })

      const response = await fetch(buildApiUrl(`/api/redemptions/search?${params}`))
      
      if (response.ok) {
        const data = await response.json()
        // console.log('🔍 Flight search response:', data);
        
        // Additional frontend filtering to ensure we only show the correct redemption types
        let filteredResults = data.redemptions || [];
        filteredResults = filteredResults.filter(r => r.redemption_type === 'flight' && r.flight);
        
        // Store all results and update displayed results (start with page 1 = 3 results)
        setAllResults(filteredResults)
        updateDisplayedResults(filteredResults, 1)
        
        // console.log('🔍 Filtered flight results:', filteredResults);
        setSearchResults(filteredResults)
        
        // Calculate aggregated data based on ALL results (not just displayed ones)
        if (filteredResults && filteredResults.length > 0) {
          const cppValues = filteredResults
            .map(r => r.value_cpp)
            .filter(cpp => cpp && cpp > 0)
          
          const pointsValues = filteredResults
            .map(r => r.points_spent)
            .filter(points => points > 0)
          
          if (cppValues.length > 0 && pointsValues.length > 0) {
            const avgCpp = cppValues.reduce((sum, cpp) => sum + cpp, 0) / cppValues.length
            const lowestPoints = Math.min(...pointsValues)
            const modePoints = calculateMode(pointsValues)
            
            setAggregatedData({
              averageCpp: avgCpp,
              totalRedemptions: filteredResults.length,
              lowestPoints: lowestPoints,
              modePoints: modePoints
            })
          }
        }
      } else {
        // console.error('Failed to fetch search results')
        setSearchResults([])
        setAllResults([])
        setDisplayedResults([])
        setCurrentPage(1)
        setAggregatedData({
          averageCpp: 0,
          totalRedemptions: 0,
          lowestPoints: 0,
          modePoints: 0
        })
      }
    } catch (error) {
      // console.error('Error searching redemptions:', error)
      setSearchResults([])
      setAllResults([])
      setDisplayedResults([])
      setCurrentPage(1)
    } finally {
      setLoading(false)
    }
  }

  const handleHotelSearch = async () => {
    if (!hotelSearchCriteria.hotelName) {
      alert('Please fill in hotel name')
      return
    }

    setLoading(true)
    setHasSearched(true)

    try {
      // Build query parameters for hotels
      const params = new URLSearchParams({
        to: hotelSearchCriteria.hotelName,
        type: 'hotel',
        orderBy: 'created_at',
        sort: 'desc'
      })

      const response = await fetch(buildApiUrl(`/api/redemptions/search?${params}`))
      
      if (response.ok) {
        const data = await response.json()
        // console.log('🔍 Hotel search response:', data);
        
        // Additional frontend filtering to ensure we only show the correct redemption types
        let filteredResults = data.redemptions || [];
        filteredResults = filteredResults.filter(r => r.redemption_type === 'hotel' && r.hotel);
        
        // Store all results and update displayed results (start with page 1 = 3 results)
        setAllResults(filteredResults)
        updateDisplayedResults(filteredResults, 1)
        
        // console.log('🔍 Filtered hotel results:', filteredResults);
        setSearchResults(filteredResults)
        
        // Calculate aggregated data based on ALL results (not just displayed ones)
        if (filteredResults && filteredResults.length > 0) {
          const cppValues = filteredResults
            .map(r => r.value_cpp)
            .filter(cpp => cpp && cpp > 0)
          
          const pointsValues = filteredResults
            .map(r => r.points_spent)
            .filter(points => points > 0)
          
          if (cppValues.length > 0 && pointsValues.length > 0) {
            const avgCpp = cppValues.reduce((sum, cpp) => sum + cpp, 0) / cppValues.length
            const lowestPoints = Math.min(...pointsValues)
            const modePoints = calculateMode(pointsValues)
            
            setAggregatedData({
              averageCpp: avgCpp,
              totalRedemptions: filteredResults.length,
              lowestPoints: lowestPoints,
              modePoints: modePoints
            })
          }
        }
      } else {
        // console.error('Failed to fetch search results')
        setSearchResults([])
        setAllResults([])
        setDisplayedResults([])
        setCurrentPage(1)
        setAggregatedData({
          averageCpp: 0,
          totalRedemptions: 0,
          lowestPoints: 0,
          modePoints: 0
        })
      }
    } catch (error) {
      // console.error('Error searching redemptions:', error)
      setSearchResults([])
      setAllResults([])
      setDisplayedResults([])
      setCurrentPage(1)
    } finally {
      setLoading(false)
    }
  }

  const formatCpp = (cpp) => {
    return cpp ? `${cpp.toFixed(2)}¢` : 'N/A'
  }

  const formatCurrency = (amount) => {
    return amount ? `$${parseFloat(amount).toFixed(2)}` : 'N/A'
  }

  // Helper function to calculate mode
  const calculateMode = (values) => {
    if (values.length === 0) return 0
    
    const frequency = {}
    let maxFreq = 0
    let mode = values[0]
    
    values.forEach(value => {
      frequency[value] = (frequency[value] || 0) + 1
      if (frequency[value] > maxFreq) {
        maxFreq = frequency[value]
        mode = value
      }
    })
    
    return mode
  }

  return (
    <div className="min-h-screen font-inter" style={{ background: 'linear-gradient(to bottom, #D9E4F8, #ffffff)' }}>
      {/* Header */}
      <AuthHeader />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('flights')}
                              className={'py-4 px-1 border-b-2 font-medium text-sm ' + (
                  activeTab === 'flights'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
            >
              <PaperAirplaneIcon className="w-5 h-5 inline mr-2" />
              FLIGHTS
            </button>
            <button
              onClick={() => setActiveTab('hotels')}
                              className={'py-4 px-1 border-b-2 font-medium text-sm ' + (
                  activeTab === 'hotels'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
            >
              <BuildingOfficeIcon className="w-5 h-5 inline mr-2" />
              HOTELS
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Form */}
        <div className="py-20 rounded-lg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl text-gray-900 mb-8" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.03em', lineHeight: '1.3' }}>
              Find Out What's A Good Deal For Your Next Trip
            </h2>
            
            {activeTab === 'flights' ? (
              // Flight Search Form
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Origin</label>
                  <MapPinIcon className="absolute left-3 top-11 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="LAX"
                    value={flightSearchCriteria.from}
                    onChange={(e) => setFlightSearchCriteria({...flightSearchCriteria, from: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm h-12"
                  />
                </div>
                
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Destination</label>
                  <MapPinIcon className="absolute left-3 top-11 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="JFK"
                    value={flightSearchCriteria.to}
                    onChange={(e) => setFlightSearchCriteria({...flightSearchCriteria, to: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm h-12"
                  />
                </div>
                
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Cabin Class</label>
                  <UserGroupIcon className="absolute left-3 top-11 h-5 w-5 text-gray-400" />
                  <select
                    value={flightSearchCriteria.cabinClass}
                    onChange={(e) => setFlightSearchCriteria({...flightSearchCriteria, cabinClass: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm h-12"
                  >
                    {cabinClassOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="relative">
                  <div className="h-7"></div>
                  <button
                    onClick={handleFlightSearch}
                    disabled={loading}
                    className="px-8 py-3 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 flex items-center justify-center h-12"
                    style={{ backgroundColor: '#548BCB' }}
                  >
                    <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
                    {loading ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>
            ) : (
              // Hotel Search Form
              <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-center items-center">
                <div className="relative w-full sm:w-auto sm:min-w-80">
                  <BuildingOfficeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Hotel Name"
                    value={hotelSearchCriteria.hotelName}
                    onChange={(e) => handleHotelNameChange(e.target.value)}
                    onKeyDown={handleHotelKeyDown}
                    onFocus={() => {
                      if (hotelSearchCriteria.hotelName.trim().length >= 2) {
                        setShowHotelDropdown(true)
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding dropdown to allow for clicks
                      setTimeout(() => setShowHotelDropdown(false), 200)
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm h-12"
                  />
                  
                  {/* Hotel dropdown */}
                  {showHotelDropdown && filteredHotels.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredHotels.map((hotel, index) => (
                        <div
                          key={hotel.id}
                                                      className={'px-3 py-2 cursor-pointer text-sm ' + (
                              index === selectedHotelIndex 
                                ? 'bg-primary-500 text-white' 
                                : 'hover:bg-gray-100'
                            )}
                          onClick={() => handleHotelSelect(hotel)}
                          onMouseEnter={() => setSelectedHotelIndex(index)}
                        >
                          <div className="font-medium">{hotel.name}</div>
                          <div className="text-xs text-gray-500">
                            {hotel.city && hotel.city}
                            {hotel.brand && hotel.city && ' • '}
                            {hotel.brand && hotel.brand}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={handleHotelSearch}
                  disabled={loading}
                  className="inline-flex items-center justify-center px-8 py-3 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#548BCB' }}
                >
                  <MagnifyingGlassIcon className="w-6 h-6 mr-2" />
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Section - Search Results or Redemption Reference */}
        <section className="py-16 mt-8 rounded-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {hasSearched ? (
              // Show Search Results
              <div>
                <h3 className="text-2xl text-gray-900 text-center mb-12" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.02em', lineHeight: '1.3' }}>
                  {activeTab === 'flights' ? 'Flight' : 'Hotel'} Search Results
                </h3>
                
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Searching for {activeTab === 'flights' ? 'flight' : 'hotel'} redemptions...</p>
                  </div>
                ) : displayedResults.length > 0 ? (
                  <>
                    {/* Aggregated Data */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                      <div className="bg-white rounded-lg p-6 text-center shadow-sm">
                        <p className="text-sm font-medium text-gray-500">Average CPP</p>
                        <p className="text-2xl font-bold text-primary-600">{formatCpp(aggregatedData.averageCpp)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-6 text-center shadow-sm">
                        <p className="text-sm font-medium text-gray-500">Total Redemptions</p>
                        <p className="text-2xl font-bold text-gray-900">{aggregatedData.totalRedemptions}</p>
                      </div>
                      <div className="bg-white rounded-lg p-6 text-center shadow-sm">
                        <p className="text-sm font-medium text-gray-500">Mode Points</p>
                        <p className="text-2xl font-bold text-purple-600">{aggregatedData.modePoints.toLocaleString()}</p>
                      </div>
                      <div className="bg-white rounded-lg p-6 text-center shadow-sm">
                        <p className="text-sm font-medium text-gray-500">Lowest Points</p>
                        <p className="text-2xl font-bold text-red-600">{aggregatedData.lowestPoints.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Redemptions Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                      {displayedResults
                        .filter(redemption => {
                          // Double-check that we only show the correct redemption types
                          if (activeTab === 'hotels') {
                            return redemption.redemption_type === 'hotel' && redemption.hotel;
                          } else {
                            return redemption.redemption_type === 'flight' && redemption.flight;
                          }
                        })
                        .map((redemption) => (
                          activeTab === 'hotels' ? (
                            // For hotels tab, show only hotel redemptions
                            <HotelRefCard
                              key={redemption.id}
                              hotel={redemption.hotel.hotel}
                              checkInDate={redemption.hotel.check_in_date}
                              checkOutDate={redemption.hotel.check_out_date}
                              price={redemption.cash_price}
                              points={redemption.points_spent}
                              cpp={redemption.value_cpp}
                              addedDate={redemption.created_at}
                            />
                          ) : (
                            // For flights tab, show only flight redemptions
                            <FlightRefCard
                              key={redemption.id}
                              flight={redemption.flight}
                              price={redemption.cash_price}
                              points={redemption.points_spent}
                              cpp={redemption.value_cpp}
                              addedDate={redemption.created_at}
                              loyaltyCurrency={redemption.program?.loyalty_currency}
                            />
                          )
                        ))}
                    </div>

                    {/* Load More Button */}
                    {hasMoreResults() && (
                      <div className="text-center mt-8">
                        <button
                          onClick={loadMoreResults}
                          className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors shadow-lg hover:shadow-xl"
                        >
                          Load More Results
                          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <p className="text-sm text-gray-500 mt-2">
                          Showing {displayedResults.length} of {allResults.length} results
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">No {activeTab === 'flights' ? 'flight' : 'hotel'} redemptions found for your search criteria</p>
                    <p className="text-sm text-gray-400">Try adjusting your search parameters</p>
                  </div>
                )}
              </div>
            ) : (
              // Show Redemption Reference (default view)
              <div>
                <h3 className="text-2xl text-gray-900 text-center mb-12" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.02em', lineHeight: '1.3' }}>Redemption Reference</h3>
                


                {/* Top Loyalty Programs Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {globalStatsLoading ? (
                    // Loading skeleton
                    Array.from({ length: 6 }).map((_, index) => (
                      <div key={index} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
                        <div className="h-4 bg-gray-200 rounded mb-3"></div>
                        <div className="h-6 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded"></div>
                      </div>
                    ))
                  ) : globalStats.topPrograms.length > 0 ? (
                    globalStats.topPrograms.map((program, index) => (
                      <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-lg font-semibold text-gray-900">{program.name}</h5>
                          <span className="text-sm text-gray-500">#{index + 1}</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Avg CPP:</span>
                            <span className="text-lg font-semibold text-primary-600">{program.avgCpp.toFixed(1)}¢</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Redemptions:</span>
                            <span className="text-sm font-medium text-gray-900">{program.count}</span>
                          </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${globalStats.bestRecord > 0 ? (program.avgCpp / globalStats.bestRecord) * 100 : 0}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 text-center">
                            {globalStats.bestRecord > 0 ? `${((program.avgCpp / globalStats.bestRecord) * 100).toFixed(0)}% of best` : 'N/A'}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    // No programs available
                    <div className="col-span-full text-center py-12">
                      <p className="text-gray-500 mb-2">No loyalty program data available</p>
                      <p className="text-sm text-gray-400">Add redemptions to see program statistics</p>
                    </div>
                  )}
                </div>

                {/* Total Redemptions Info */}
                {!globalStatsLoading && globalStats.totalRedemptions > 0 && (
                  <div className="text-center mt-8">
                    <p className="text-gray-600">
                      Based on <span className="font-semibold text-gray-900">{globalStats.totalRedemptions.toLocaleString()}</span> {activeTab === 'flights' ? 'flight' : 'hotel'} redemptions across all loyalty programs
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default CheckRedemptionValue
